import { useMemo, useState } from "react";

import { BurgerMenu } from "shared/UI/BurgerMenu/BurgerMenu";
import { ReactComponent as Filter } from "@icons/filter.svg";
import { Hideable } from "shared/UI/Hideable/Hideable";
import Button from "shared/UI/Buttons/Button";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import classNames from "classnames";
import { trackFilteredInterests } from "core/monitoring/Events";
import useComponentVisible from "shared/UI/Hooks/useComponentVisible";
import { useGetUserInterestPreferencesFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { useTranslation } from "react-i18next";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { Checkbox, CheckBoxPosition } from "shared/UI/Checkbox/Checkbox";

type ResourceFilterMenuProps = {
  preSelectedFilters?: string[];
  onApply: (filters: string[]) => void;
};

export const ResourceFilterMenu = ({ preSelectedFilters = [], onApply }: ResourceFilterMenuProps) => {
  const { t } = useTranslation();
  const { insetPadding, isMobileApp } = useMobilePlatform();
  const { targetAreaRef, isComponentVisible, setIsComponentVisible } = useComponentVisible<HTMLDivElement>(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(preSelectedFilters);
  const { data, error, isLoading, mutate: refresh } = useGetUserInterestPreferencesFunctionRequest({}, { showErrorIfStale: false });

  const sortedFilters = useMemo(() => {
    return data?.interestPreferences.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
  }, [data]);

  const handleClickFilter = () => {
    trackFilteredInterests(sortedFilters, selectedFilters);
    onApply(selectedFilters);
    setIsComponentVisible(false);
  };

  return (
    <div>
      <button
        className={classNames("rounded-full mb-2 px-2 py-2 drop-shadow-lg bg-white", { "pointer-events-none": isComponentVisible })}
        aria-label={t("resources.articleLibraryFilters.openMenuButtonLabel")}
        onClick={() => setIsComponentVisible(true)}
      >
        <Filter className="h-7 md:h-9 text-brand hover:text-primary-600" />
        <Hideable hidden={preSelectedFilters.length === 0}>
          <div
            className={classNames(
              "pointer-events-none text-white absolute top-[-8px] md:top-[-10px] right-[-4px] md:right-[-5px]",
              "rounded-full bg-accent-600  w-[18px] md:w-[22px] h-[18px] md:h-[22px] leading-[18px] md:leading-[22px] font-bold"
            )}
          >
            {preSelectedFilters.length}
          </div>
        </Hideable>
      </button>
      <div ref={targetAreaRef}>
        <BurgerMenu
          fullHeight={isMobileApp}
          topInsetPadding={insetPadding?.top}
          isOpen={isComponentVisible}
          onCloseClick={() => setIsComponentVisible(false)}
          onCloseFinished={() => setSelectedFilters(preSelectedFilters)}
          heading={t("resources.articleLibraryFilters.heading")}
          closeButtionAriaLabel={t("resources.articleLibraryFilters.closeMenuButtonLabel")}
        >
          <ViewStateWrapper loading={isLoading} error={!!error} errorMessage={t("resources.articleLibraryFilters.error")} onRetry={refresh}>
            <div className="flex flex-col pt-6">
              {sortedFilters.map((filter) => {
                return (
                  <span className="sorted-filters" key={filter.categoryId}>
                    <Checkbox
                      checked={selectedFilters.includes(filter.categoryId)}
                      label={filter.name}
                      onChange={(e) => {
                        const isSelected = e.target.checked;
                        if (isSelected) {
                          setSelectedFilters([...selectedFilters, filter.categoryId]);
                        } else {
                          setSelectedFilters(selectedFilters.filter((x) => x !== filter.categoryId));
                        }
                      }}
                      position={CheckBoxPosition.Right}
                    />
                  </span>
                );
              })}
              <div className="mt-4 mb-12">
                <Button onClick={handleClickFilter} text={t("resources.articleLibraryFilters.applyButtonLabel")} />
              </div>
            </div>
          </ViewStateWrapper>
        </BurgerMenu>
      </div>
    </div>
  );
};
